import { useState } from 'react';
import {
  ArrowRight,
  ShieldCheck,
  Star,
  Check,
  X,
  DollarSign,
  Lock,
  Wallet,
  Menu,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import TreeQuickForm from '../components/TreeQuickForm';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_270b8cc9-1693-407c-b15e-5414d2b83f2e/artifacts/qe9vta95_LOGO-FIRMA-PNG-1-branco-258x278-1%20%282%29.png";

// Residential tree-service photography (Unsplash — free commercial license).
// ponytail: hotlinked from the Unsplash CDN. Swap for the client's own crew shots when available.
const IMG_HERO = "https://images.unsplash.com/photo-1754322449185-31f56117ed87?auto=format&fit=crop&w=1600&q=70";

// Decorative botanical branch — soft, low-opacity organic accent for section corners.
function LeafDecor({ className = '' }) {
  return (
    <svg viewBox="0 0 200 200" fill="none" className={className} aria-hidden="true">
      <path d="M100 190C100 120 60 70 20 45" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
      <path d="M70 120c-18-6-30-2-46 8 12 14 26 18 44 12" fill="currentColor" opacity="0.5" />
      <path d="M60 95c-16-8-28-6-44 2 10 15 24 20 42 16" fill="currentColor" opacity="0.4" />
      <path d="M52 70c-15-9-27-9-42-3 9 16 22 22 40 20" fill="currentColor" opacity="0.35" />
      <path d="M100 190C100 120 140 70 180 45" stroke="currentColor" strokeWidth="3" strokeLinecap="round" opacity="0.7" />
      <path d="M130 120c18-6 30-2 46 8-12 14-26 18-44 12" fill="currentColor" opacity="0.35" />
      <path d="M140 95c16-8 28-6 44 2-10 15-24 20-42 16" fill="currentColor" opacity="0.28" />
    </svg>
  );
}

// "Exclusive Opportunity" pay-per-lead variant of the tree-service LP. Same moss
// theme + TreeQuickForm as /tree-service; different angle: no retainers, exclusive
// territory, pay-per-lead, comparison table + testimonials. Served at /tree-service-exclusive.
export default function ExclusiveTreePage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const scrollTo = (sectionId) => {
    const el = document.getElementById(sectionId);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
    setMobileMenuOpen(false);
  };
  const goToForm = () => scrollTo('lead-form');

  const benefits = [
    { icon: Lock, title: '100% Exclusive Leads', text: 'Never shared with your competitors.' },
    { icon: Wallet, title: 'Zero Ad Spend Risk', text: 'We invest our own marketing dollars to find your jobs.' },
    { icon: DollarSign, title: 'Pay-Per-Lead Model', text: 'No hidden fees, no long contracts. Pay only for what we deliver.' },
  ];

  const traditional = [
    'Expensive monthly retainers whether you get jobs or not.',
    'Shared leads sold to 4 or 5 competitors at the same time.',
    'You risk your own ad budget on campaigns that might fail.',
  ];

  const ourWay = [
    'Zero retainers. You only pay per qualified estimate request.',
    '100% exclusive leads. Once a territory is yours, it\'s closed.',
    'We pay for all marketing. If we don\'t deliver, we don\'t get paid.',
  ];

  const testimonials = [
    {
      quote: 'We went from 2 crews to 5 in less than 6 months. I was skeptical because every agency promises exclusive leads and delivers junk. But the Tree Lead System changed our game. The estimates land straight on my calendar, already filtered. My crews are busy year-round, even during the slow season.',
      name: 'John Miller',
      company: 'Owner, Miller & Sons Tree Service',
      state: 'Georgia',
    },
    {
      quote: 'Finally, an agency that puts their money where their mouth is. No monthly retainers meant I had zero risk to try them out. In the first 30 days, we closed three big commercial tree removal jobs that paid for the leads tenfold. Best marketing decision we\'ve ever made.',
      name: 'Dave Rutherford',
      company: 'Founder, Apex Tree Care',
      state: 'Texas',
    },
    {
      quote: 'Stop buying shared leads. This is the real deal. I used to waste hours racing 4 other competitors to call the same homeowner on Angi. With Tree Lead System, the leads are 100% mine. The closing rate is incredibly high because the customer is actually waiting for our estimate.',
      name: 'Carlos Mendez',
      company: 'Owner, Mendez Tree & Landscaping',
      state: 'Florida',
    },
    {
      quote: 'An extra $45k in revenue in our first 90 days. The pay-per-lead model is beautiful. We know exactly what our cost of acquisition is, which makes scaling our operation simple. FIRMA handles the marketing, we handle the chainsaws. Perfect partnership.',
      name: 'Sarah Jenkins',
      company: 'Operations Manager, Elite Tree Experts',
      state: 'Ohio',
    },
  ];

  const CTA_TEXT = 'Secure My Exclusive Territory Now';

  return (
    <div className="min-h-screen overflow-x-hidden bg-[#F6F3EC] font-sans text-[#26331F]">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-[#16241A] border-b border-[#26402A]" data-testid="header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2.5" data-testid="logo">
              <img src={LOGO_URL} alt="The Tree Lead System" className="h-9 w-auto" />
              <span className="hidden sm:block text-sm font-bold tracking-wide text-white uppercase">Tree Lead System</span>
            </div>

            <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
              <button onClick={() => scrollTo('compare')} className="text-xs font-semibold uppercase tracking-wider text-[#A9C97E] hover:text-white transition-colors">Why Us</button>
              <button onClick={() => scrollTo('reviews')} className="text-xs font-semibold uppercase tracking-wider text-[#A9C97E] hover:text-white transition-colors">Results</button>
            </nav>

            <div className="hidden lg:block">
              <Button onClick={goToForm} className="btn-moss !py-2.5 !px-5 text-xs uppercase tracking-wider" data-testid="header-cta">
                {CTA_TEXT}
              </Button>
            </div>

            <button className="lg:hidden p-2 text-white" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-toggle">
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="lg:hidden bg-[#16241A] border-t border-[#26402A] px-4 py-6 space-y-4" data-testid="mobile-menu">
            <button onClick={() => scrollTo('compare')} className="block w-full text-left text-sm font-semibold uppercase tracking-wider text-[#A9C97E]">Why Us</button>
            <button onClick={() => scrollTo('reviews')} className="block w-full text-left text-sm font-semibold uppercase tracking-wider text-[#A9C97E]">Results</button>
            <Button onClick={goToForm} className="btn-moss w-full text-xs uppercase tracking-wider">{CTA_TEXT}</Button>
          </div>
        )}
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden bg-[#16241A]" data-testid="hero-section">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${IMG_HERO})` }} aria-hidden="true" />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(18,32,22,0.82) 0%, rgba(20,36,25,0.88) 55%, rgba(14,26,18,0.94) 100%)' }} aria-hidden="true" />
        <LeafDecor className="pointer-events-none absolute -top-6 -left-8 w-56 h-56 text-[#3E5A2C] opacity-40 rotate-12" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Copy */}
            <div className="min-w-0 text-center lg:text-left">
              <span className="inline-flex max-w-full items-center justify-center gap-2 border border-[#4A6A2A] bg-[#1D3020]/60 rounded-2xl sm:rounded-full px-4 py-1.5 text-[11px] sm:text-xs font-semibold uppercase tracking-wider text-[#C7DEA1] leading-snug whitespace-normal text-center mb-6" data-testid="hero-badge">
                <Star className="w-3.5 h-3.5 flex-shrink-0" /> Exclusive Opportunity for Tree Service Companies
              </span>

              <h1 className="font-display font-black leading-[1.05] sm:leading-[1.02] tracking-tight text-white text-[2rem] sm:text-5xl lg:text-[3.4rem] mb-6 break-words" data-testid="hero-headline">
                Stop Riding the Revenue Rollercoaster. Get <span className="text-[#A9C97E]">Exclusive Tree Removal Leads</span> Booked Directly on Your Calendar.
              </h1>

              <p className="text-base sm:text-lg text-[#C7D4B4] mb-8 max-w-xl mx-auto lg:mx-0" data-testid="hero-sub">
                No monthly retainers. No shared leads. No setup fees. You only pay for real, pre-qualified estimate requests from homeowners in your specific territory who need tree work done now.
              </p>

              <div className="space-y-3 mb-8 text-left max-w-md mx-auto lg:mx-0" data-testid="hero-benefits">
                {benefits.map((b, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-[#5E7D3A] flex items-center justify-center flex-shrink-0 mt-0.5"><Check className="w-3.5 h-3.5 text-white" /></span>
                    <p className="text-[#EAF0DC] text-sm sm:text-base"><span className="font-bold text-white">{b.title}</span> — {b.text}</p>
                  </div>
                ))}
              </div>

              <Button onClick={goToForm} className="btn-moss uppercase tracking-wider" data-testid="hero-cta">
                {CTA_TEXT} <ArrowRight className="w-5 h-5" />
              </Button>
            </div>

            {/* Form */}
            <div className="flex min-w-0 justify-center lg:justify-end" data-testid="hero-form">
              <TreeQuickForm id="lead-form" />
            </div>
          </div>
        </div>
      </section>

      {/* Comparison */}
      <section id="compare" className="relative py-20 lg:py-28 bg-[#F6F3EC]" data-testid="compare-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <p className="eyebrow-moss justify-center mb-3"><span className="inline-block w-6 h-px bg-[#5E7D3A]" /> The Difference</p>
            <h2 className="font-display text-3xl sm:text-5xl font-black text-[#22301F] leading-tight max-w-3xl mx-auto" data-testid="compare-headline">
              Why Traditional Marketing Agencies Fail You <span className="text-[#5E7D3A]">(And How We Are Different)</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6" data-testid="compare-grid">
            {/* Traditional */}
            <div className="bg-white rounded-2xl border border-[#E7E0D2] p-8 shadow-[0_10px_30px_-20px_rgba(34,48,31,0.4)]">
              <h3 className="font-display text-xl font-bold text-[#8A5A4A] mb-1">The Traditional Way</h3>
              <p className="text-xs font-semibold uppercase tracking-wider text-[#B08578] mb-6">Risk on You</p>
              <div className="space-y-4">
                {traditional.map((line, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-[#F3E3DE] flex items-center justify-center flex-shrink-0 mt-0.5"><X className="w-3.5 h-3.5 text-[#B5654E]" /></span>
                    <p className="text-sm text-[#6B6558] leading-relaxed">{line}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Our way */}
            <div className="bg-[#16241A] rounded-2xl border border-[#2E4620] p-8 shadow-[0_20px_50px_-20px_rgba(15,26,18,0.6)] relative overflow-hidden">
              <LeafDecor className="pointer-events-none absolute -top-4 -right-4 w-32 h-32 text-[#2E4620] opacity-50" />
              <h3 className="font-display text-xl font-bold text-white mb-1">The Tree Lead System Way</h3>
              <p className="text-xs font-semibold uppercase tracking-wider text-[#A9C97E] mb-6">Risk on Us</p>
              <div className="space-y-4 relative">
                {ourWay.map((line, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-[#5E7D3A] flex items-center justify-center flex-shrink-0 mt-0.5"><Check className="w-3.5 h-3.5 text-white" /></span>
                    <p className="text-sm text-[#EAF0DC] leading-relaxed">{line}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="text-center mt-12">
            <Button onClick={goToForm} className="btn-moss uppercase tracking-wider mx-auto" data-testid="compare-cta">
              {CTA_TEXT} <ArrowRight className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="reviews" className="relative py-20 lg:py-28 bg-[#ECF0E2]" data-testid="reviews-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <p className="eyebrow-moss justify-center mb-3"><span className="inline-block w-6 h-px bg-[#5E7D3A]" /> Real Owners, Real Results</p>
            <h2 className="font-display text-3xl sm:text-5xl font-black text-[#22301F] leading-tight" data-testid="reviews-headline">
              Tree Companies Growing With <span className="text-[#5E7D3A]">Exclusive Leads</span>
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 gap-6" data-testid="review-cards">
            {testimonials.map((t, i) => (
              <div key={i} className="bg-white rounded-2xl border border-[#E1E5D4] p-7 shadow-[0_10px_30px_-22px_rgba(34,48,31,0.45)] flex flex-col" data-testid={`review-${i}`}>
                <div className="flex gap-0.5 mb-4">
                  {[0, 1, 2, 3, 4].map((s) => (
                    <Star key={s} className="w-4 h-4 text-[#E0A93B] fill-[#E0A93B]" />
                  ))}
                </div>
                <p className="text-[#3F4A35] leading-relaxed mb-6 flex-1">“{t.quote}”</p>
                <div className="pt-4 border-t border-[#E7E0D2]">
                  <p className="font-bold text-[#22301F]">{t.name}</p>
                  <p className="text-sm text-[#6B6558]">{t.company} <span className="text-[#5E7D3A]">· {t.state}</span></p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section id="cta" className="relative overflow-hidden bg-[#16241A]" data-testid="cta-section">
        <div className="absolute inset-0 bg-cover bg-center opacity-30" style={{ backgroundImage: `url(${IMG_HERO})` }} aria-hidden="true" />
        <div className="absolute inset-0" style={{ background: 'radial-gradient(120% 100% at 50% 100%, rgba(30,50,32,0.7) 0%, rgba(14,26,18,0.96) 65%)' }} aria-hidden="true" />
        <LeafDecor className="pointer-events-none absolute -bottom-8 -left-8 w-56 h-56 text-[#2E4620] opacity-50" />
        <div className="relative max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28 text-center">
          <div className="w-14 h-14 rounded-2xl bg-[#1D3020] border border-[#3A5528] flex items-center justify-center mx-auto mb-6">
            <ShieldCheck className="w-7 h-7 text-[#A9C97E]" />
          </div>
          <h2 className="font-display text-3xl sm:text-5xl font-black text-white mb-5" data-testid="cta-headline">
            Your Territory Is <span className="text-[#A9C97E]">Waiting.</span>
          </h2>
          <p className="text-lg text-[#B7C4A6] mb-8">
            One company per territory — zero risk to start. We invest our own marketing dollars. You only pay for real, pre-qualified estimate requests. Secure your market before a competitor does.
          </p>
          <Button onClick={goToForm} className="btn-moss uppercase tracking-wider mx-auto" data-testid="cta-button">
            {CTA_TEXT} <ArrowRight className="w-5 h-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0F1A12] py-10 border-t border-[#26402A]" data-testid="footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-2.5">
            <img src={LOGO_URL} alt="The Tree Lead System" className="h-8 w-auto" />
            <span className="text-sm text-[#A9C97E]">The Tree Lead System™</span>
          </div>
          <p className="text-xs text-[#7C8B6E]">© {new Date().getFullYear()} The Tree Lead System™. One company per territory.</p>
        </div>
      </footer>
    </div>
  );
}
